import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { viewerAPI } from 'api/viewer';
import { buildDrillToURL, ElementViewerDataResponse } from 'components/element-viewer';
import MetricViewer, { MetricViewerDataType } from 'components/metric/MetricViewer';
import ExternalReportViewer, {
    ExternalReportViewerDataType,
    useGetSegmentValueIdFromFilters,
} from 'app/extreport/ExternalReportViewer';
import { FilterType, useApplyFilterValueMap } from 'components/external-reference/Filter';
import { FormComponentValue } from 'components/common/form/layout/control';
import { ElementTypeNS } from 'tools/elementType';
import { favoritesAPI } from 'api/favorites';
import ReportViewer, { ReportViewerDataType } from 'app/report/ReportViewer';
import ExternalContentViewer, { ExternalContentViewerDataType } from 'app/ext-content/ExternalContentViewer';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { Box, Stack } from '@mui/material';
import { usePrepareViewerData } from 'components/element-viewer/hooks/usePrepareViewerData';

import {
    setElementViewerVisibility,
    setIsApp,
    setElementViewerDimensions,
    setElementViewerCustomEvents,
} from 'store/elementViewerSlice';

interface IProps {
    element_id: number;
    segment_id?: number;
    height?: number;
    width?: number;
    container_id?: string;
    visibility?: {
        header?: boolean;
        title?: boolean;
        description?: boolean;
        certification?: boolean;
        wall?: boolean;
        extLink?: boolean;
        actionsBar?: boolean;
        iNotification?: boolean;
        iOpenExt?: boolean;
        bookmark?: boolean;
        segment?: boolean;
        eFilters?: boolean;
        summary?: boolean;
        control_buttons?: {
            info?: boolean;
            lineage?: boolean;
            favorite?: boolean;
            download?: boolean;
            open_external_report?: boolean;
            full_screen?: boolean;
            edit?: boolean;
            expert_analysis?: boolean;
            rating?: boolean;
        };
        action_select?: boolean;
        action_select_buttons?: {
            share?: boolean;
            rating?: boolean;
            lineage?: boolean;
            expert_analysis?: boolean;
            notification?: boolean;
        };
    };
    custom_events?: {
        external_link_click?: boolean;
        favorite_click?: boolean;
    };
}

export default function ElementViewerWrapper({ props }: { props: IProps }) {
    const dispatch = useDispatch();

    useEffect(() => {
        if (props.visibility) {
            dispatch(setElementViewerVisibility(props.visibility));
        }
    }, [props.visibility, dispatch]);

    useEffect(() => {
        dispatch(setIsApp(true));
    }, [dispatch]);

    useEffect(() => {
        dispatch(
            setElementViewerDimensions({
                height: props.height,
                width: props.width,
            }),
        );
    }, [props.height, props.width, dispatch]);

    useEffect(() => {
        if (props.custom_events) {
            dispatch(setElementViewerCustomEvents(props.custom_events));
        }
    }, [props.custom_events, dispatch]);

    const [filters, setFilters] = useState<Array<FilterType>>([]);
    const elementId = props.element_id;
    const initialSegmentId = props.segment_id ?? 0;
    const [currentSegmentId, setCurrentSegmentId] = useState<number>(initialSegmentId);
    const [userChartOverlay, setUserChartOverlay] = useState<number>(0);

    const handleFilterChange = function (filterId: number, newValue: any) {
        const list = filters.slice();
        if (!Array.isArray(newValue)) {
            newValue = newValue == '' ? [] : [newValue];
        }
        list.find((f) => f.filterId == filterId)!.selectedValues = newValue;
        const fullList = useApplyFilterValueMap(list);
        setFilters(fullList);

        const newSegmentValueId = useGetSegmentValueIdFromFilters(fullList);
        onSegmentValueChange(newSegmentValueId);
    };

    const queryClient = useQueryClient();
    const queryKey = `elementViewer_${elementId}_${currentSegmentId}`;
    const {
        data: viewerRequestData,
        refetch,
        isRefetching,
    } = useQuery<ElementViewerDataResponse, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return viewerAPI.loadElementData(
                elementId,
                currentSegmentId,
                true,
                userChartOverlay,
                false,
                false,
                0,
                new URLSearchParams(),
                0,
                0,
            );
        },
        refetchOnMount: true,
    });

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: [queryKey] });
        };
    }, []);

    const { elementType, viewerData, setViewerData, segmentValueId, setSegmentValueId, viewerExtraData } =
        usePrepareViewerData(userChartOverlay, setUserChartOverlay, setFilters, 'viewer', viewerRequestData);

    useEffect(() => {
        if (initialSegmentId && segmentValueId !== initialSegmentId) {
            setSegmentValueId(initialSegmentId);
        }
    }, [initialSegmentId]);

    const onSegmentValueChange = (newSegmentValueId: number) => {
        if (newSegmentValueId == segmentValueId) {
            return;
        }
        setSegmentValueId(newSegmentValueId);
        setCurrentSegmentId(newSegmentValueId);
    };

    const handleFiltersChange = function (newFilters: Array<FilterType>) {
        const list = newFilters.slice();
        list.forEach((filter) => {
            if (filter.filterInput == 'text') {
                let rawValue: any = [];
                filter.selectedValues.forEach((sv) => {
                    const fv = filter.values.find((v) => v.value_id == sv);
                    if (fv) {
                        rawValue.push(fv.value);
                    }
                });

                if (rawValue.length) {
                    filter.selectedValues = rawValue;
                    filter.defaultValues = rawValue;
                }
            }
        });
        setFilters(list);
    };

    const related: Array<FormComponentValue> = useMemo<Array<FormComponentValue>>(() => {
        if (!viewerData?.elementData?.related) {
            return [];
        }

        const related = Array.isArray(viewerData.elementData.related?.related)
            ? viewerData.elementData.related.related
            : [];

        const drill = Array.isArray(viewerData.elementData.related?.drillDown)
            ? viewerData.elementData.related.drillDown
            : [];

        const interval = Array.isArray(viewerData.elementData.related?.interval)
            ? viewerData.elementData.related.interval
            : [];

        return related
            .map((r) => {
                return {
                    label: r.elementName,
                    value:
                        '/' +
                        ElementTypeNS.getPathByElementType(r.elementType) +
                        `/${r.elementId}/segment/${r.segmentId}`,
                    props: { icon: { type: 'mi', value: ElementTypeNS.getIconNameByElementType(r.elementType) } },
                };
            })
            .concat(
                drill.map((r) => {
                    return {
                        label: r.label,
                        value: buildDrillToURL(r),
                        props: { icon: { type: 'mi', value: ElementTypeNS.getIconNameByElementType(r.type) } },
                    };
                }),
            )
            .concat(
                interval.map((i) => {
                    return {
                        label: i.intervalName,
                        value:
                            '/' +
                            ElementTypeNS.getPathByElementType('internal report') +
                            `/${i.linkToElement}/segment/${i.linkToSegment}`,
                        props: {
                            icon: {
                                type: 'mi',
                                value: ElementTypeNS.getIconNameByElementType('internal report'),
                            },
                            intervalId: i.intervalId,
                            selected: i.intervalId == viewerData.elementData.row.measurementIntervalId,
                        },
                    };
                }),
            );
    }, [viewerData?.elementData?.related]);

    const handleRelatedElementSelection = (value: string) => {};

    const handleFavoritesChange = () => {
        favoritesAPI.getElementFavoritesList(elementId, segmentValueId, true).then((result) => {
            setViewerData((prevState) => {
                const newState = structuredClone(prevState);
                if (undefined !== newState) newState.elementData.inFavorites = result.slice();
                return newState;
            });
        });
    };

    if (!viewerData) {
        return <LoadingPlaceholder />;
    }

    const viewerProps = {
        embeddingType: 'viewer' as const,
        elementInfo: viewerData?.elementData,
        crumbsData: viewerData?.crumbsData,
        targets: viewerData?.targets,
        alertStatus: viewerData?.alertStatus,
        related: related,
        viewerRequestData: viewerData,
        filters: filters,
        onFiltersChange: handleFiltersChange,
        onSegmentValueChange: onSegmentValueChange,
        segmentValueId: segmentValueId,
        onFavoritesChange: handleFavoritesChange,
        onRelatedElementSelection: handleRelatedElementSelection,
        onFilterChange: handleFilterChange,
        refetch: refetch,
        setUserChartInterval: setUserChartOverlay,
    };

    return (
        <Box sx={{ height: props.height || '100%', width: props.width || '100%' }}>
            {isRefetching && <LoadingPlaceholder />}
            <Stack sx={{ height: '100%' }}>
                {(elementType == 'metric' || elementType == 'multi-metric chart') && (
                    <MetricViewer {...viewerProps} viewerRequestExtraData={viewerExtraData as MetricViewerDataType} />
                )}
                {elementType == 'external report' && (
                    <ExternalReportViewer
                        {...viewerProps}
                        viewerRequestExtraData={viewerExtraData as ExternalReportViewerDataType}
                    />
                )}
                {elementType == 'internal report' && (
                    <ReportViewer {...viewerProps} viewerRequestExtraData={viewerExtraData as ReportViewerDataType} />
                )}
                {elementType == 'other external content' && (
                    <ExternalContentViewer
                        {...viewerProps}
                        viewerRequestExtraData={viewerExtraData as ExternalContentViewerDataType}
                    />
                )}
            </Stack>
        </Box>
    );
}
